import clsx from 'clsx';
import { Suspense, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';

import { formatRecord } from './BreakdownTabs';
import type { ResultsFilters } from './FilterTypes';
import { ResultsChart, ResultsChartLoading } from './ResultsChart';
import { SeasonPicker } from './SeasonPicker';
import { RESULTS_CONTROL, RESULTS_CONTROL_IDLE, SPORT_IDS, filterUrl } from './consts';
import { useResultsSummary } from './useResultsSummary';

// Sport chips: "All sports" (the widest view) first, then the main sports in volume order
// (CBB is the largest by graded volume). The tail sports (NBA/WNBA/CFL/college baseball)
// live behind "All sports" (#2004, #2142).
const CHIPS: { key: string; label: string; url: string }[] = [
  { key: 'all', label: 'All sports', url: 'all' },
  { key: 'cbb', label: 'CBB', url: 'cbb' },
  { key: 'cfb', label: 'CFB', url: 'cfb' },
  { key: 'nfl', label: 'NFL', url: 'nfl' },
];

const SPORT_FULL: Record<string, string> = {
  '1': 'College football',
  '2': 'College basketball',
  '3': 'NFL',
  '0': 'All sports',
  cfb: 'College football',
  cbb: 'College basketball',
  nfl: 'NFL',
  all: 'All sports',
  ras: 'RAS sports',
  'nfl,cfb': 'NFL & CFB',
  'cfb,nfl': 'NFL & CFB',
};

function sportKeyForChip(sportId?: string): string {
  if (!sportId || sportId === 'ras') {
    return '';
  }
  if (sportId === '0') {
    return 'all';
  }
  // numeric → name
  const byId = Object.entries(SPORT_IDS).find(([, id]) => `${id}` === `${sportId}`)?.[0];
  return byId || sportId;
}

/** A `?startDate=` / `?endDate=` URL value (yyyyMMdd) as "Jul 1, 2020". */
function formatUrlDate(value?: string): string | null {
  if (!value || !/^\d{8}$/.test(value)) {
    return null;
  }
  const date = new Date(+value.slice(0, 4), +value.slice(4, 6) - 1, +value.slice(6, 8));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The range the view actually covers, in words. An explicit `?startDate=` / `?endDate=`
 * in the URL overrides the season (the results query uses it instead), so the label must
 * follow it: "/archive/nfl,cfb?startDate=20200701" is "since Jul 1, 2020", not "this
 * season" (#2080).
 */
export function rangePhrase(
  seasonId: string | undefined,
  sportId: string | undefined,
  seasons?: API.Season[],
  startDate?: string,
  endDate?: string,
): string {
  const start = formatUrlDate(startDate);
  const end = formatUrlDate(endDate);
  if (start && end) {
    return `${start} to ${end}`;
  }
  if (start) {
    return `since ${start}`;
  }
  if (end) {
    return `through ${end}`;
  }
  return seasonPhrase(seasonId, sportId, seasons);
}

function seasonPhrase(
  seasonId: string | undefined,
  sportId: string | undefined,
  seasons?: API.Season[],
): string {
  if (seasonId && /^\d+$/.test(seasonId)) {
    return seasons?.find((s) => `${s.id}` === `${seasonId}`)?.name || 'season';
  }
  switch (seasonId) {
    case 'all-time':
      return 'all-time';
    case 'last':
      return 'last season';
    case 'last-2':
      return 'last 2 seasons';
    case 'last-3':
      return 'last 3 seasons';
    case 'last-5':
      return 'last 5 seasons';
    default: {
      // current: the most recent season's name for a single sport, else "this season".
      const key = sportKeyForChip(sportId);
      const num = (SPORT_IDS as Record<string, number>)[key];
      if (num && seasons) {
        const latest = seasons
          .filter((s) => s.sportId === num && !s.hidden && s.endDate)
          .sort((a, b) => Date.parse(b.endDate || '') - Date.parse(a.endDate || ''))[0];
        if (latest?.name) {
          return latest.name;
        }
      }
      return 'this season';
    }
  }
}

/**
 * The results header card (#2004): the scope title ("My results" / "All results") + sub,
 * then the controls — the My/All scope toggle beside the season picker, and the sport
 * chips beneath. The stats + units chart render below them.
 *
 * The picker alone names the range in view; the card used to repeat it as its title
 * right above the picker (#2142).
 */
export function ResultsHeader({
  baseUrl,
  userId,
  filters,
  sportId,
  seasonId,
  seasons,
  scope,
  onScopeChange,
  canToggle,
  hasNoPlays = false,
  startDate,
  endDate,
  loading = false,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  sportId?: string;
  seasonId?: string;
  seasons?: API.Season[];
  // 'me' → My results, otherwise All results (the public archive feed).
  scope: 'me' | 'archive';
  onScopeChange: (scope: 'me' | 'archive') => void;
  canToggle: boolean;
  // Nothing graded and nothing pending in this view: skip the chart, which would plot
  // an empty series with an invented axis (#2004).
  hasNoPlays?: boolean;
  // The raw ?startDate= / ?endDate= (yyyyMMdd) from the URL, when the view is a custom
  // range instead of a season. The label follows them (#2080).
  startDate?: string;
  endDate?: string;
  // Hold the stats as skeletons (the page is still choosing its default scope).
  loading?: boolean;
}) {
  const summary = useResultsSummary(userId, filters, { paused: loading });
  const { wins, losses, pushes, units } = summary;
  const isLoading = summary.isLoading || loading;
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : '—';
  const chipKey = sportKeyForChip(sportId);
  const seasonIdIsNumber = !!seasonId && /^\d+$/.test(seasonId);

  // The picker label: the range, led by the sport unless a chip already shows it
  // ("2025 season" under an active NFL chip, "NFL & CFB · since Jul 1, 2020" otherwise).
  const pickerLabel = useMemo(() => {
    const range = rangePhrase(seasonId, sportId, seasons, startDate, endDate);
    // An active chip already says which sport is in view.
    if (chipKey && CHIPS.some((c) => c.key === chipKey)) {
      return range.charAt(0).toUpperCase() + range.slice(1);
    }
    const sport = SPORT_FULL[sportId || 'ras'] || 'Results';
    return `${sport} · ${range}`;
  }, [sportId, seasonId, seasons, startDate, endDate, chipKey]);

  // While the page is still choosing its default scope, name neither one, so the copy
  // never reads "My results" for a beat before settling on All (#2142).
  const heading = loading ? 'Results' : scope === 'me' ? 'My results' : 'All results';
  const sub = loading
    ? ' '
    : scope === 'me'
      ? 'Only the plays on your account'
      : 'Every play RAS has released';

  return (
    <div className="mb-3 rounded-2xl border border-zinc-950/[0.08] bg-white p-4 shadow-xs sm:p-5 dark:border-white/10 dark:bg-zinc-900">
      {/* The scope heading. Below md the app header bar already shows it ("My Results" /
          "All Results"), so only the sub-line shows here (#2142). */}
      <div className="mb-3 sm:mb-4">
        <h1 className="hidden font-bold text-2xl text-zinc-900 tracking-tight md:block dark:text-white">
          {heading}
        </h1>
        <p className="text-xs text-zinc-400 md:mt-0.5 dark:text-zinc-500">{sub}</p>
      </div>

      {/* Two rows: scope + the range you're in on top, the sport chips beneath. All share
          RESULTS_CONTROL sizing. The top row never wraps (the picker label truncates
          instead), so a phone gets two filter rows, not three (#2142). */}
      <div className="flex flex-col gap-2">
        <div className="flex min-w-0 flex-nowrap items-center gap-2">
          {canToggle && (
            <div className="inline-flex h-8 shrink-0 items-center rounded-lg border border-zinc-950/15 bg-zinc-50 p-0.5 dark:border-white/15 dark:bg-white/5">
              {(['me', 'archive'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onScopeChange(s)}
                  aria-pressed={!loading && scope === s}
                  className={twMerge(
                    'inline-flex h-full items-center rounded-md px-3 font-semibold text-xs',
                    !loading && scope === s
                      ? 'bg-emerald-700 text-white'
                      : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white',
                  )}
                >
                  {/* Short on a phone so the range beside it isn't truncated away. */}
                  <span className="sm:hidden">{s === 'me' ? 'Mine' : 'All'}</span>
                  <span className="hidden sm:inline">
                    {s === 'me' ? 'My results' : 'All results'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* The picker names the view you're in ("All sports · all-time"); the chips
              below narrow it, so it reads scope-then-refinement (#2004). */}
          <SeasonPicker
            baseUrl={baseUrl}
            sportId={sportId}
            seasonId={seasonId}
            seasons={seasons}
            label={pickerLabel}
          />
        </div>

        <div className="-mx-1 flex flex-nowrap items-center gap-2 overflow-x-auto px-1 [scrollbar-width:none]">
          {CHIPS.map((chip) => {
            const active = chipKey === chip.key;
            return (
              <a
                key={chip.key}
                href={filterUrl(baseUrl, chip.url, seasonIdIsNumber ? undefined : seasonId, '')}
                aria-pressed={active}
                className={twMerge(
                  RESULTS_CONTROL,
                  active ? 'border-emerald-700 bg-emerald-700 text-white' : RESULTS_CONTROL_IDLE,
                )}
              >
                {chip.label}
              </a>
            );
          })}
        </div>
      </div>

      {/* Record / win rate / units, then the cumulative units chart — all in the one
          header card (#2004). A natural flex row (not forced equal columns) so the record
          never gets squeezed into a wrap/clip; stats wrap as whole units if too narrow. */}
      {/* One line, never re-wrapped. Wrapping here was data-dependent — a wide record
          ("4892-3011-6") pushed Units onto a second line while a narrow one ("0-0") did
          not, so the row grew and shrank as you moved between sports and everything
          below the card jumped with it. The type scales to fit instead (#2004). */}
      <div className="mt-5 flex flex-nowrap items-start gap-x-6 sm:gap-x-10">
        <Stat label="Record">
          <Skeleton loading={isLoading} className="inline-block">
            {isLoading ? '000-000' : formatRecord({ w: wins, l: losses, p: pushes, u: 0 })}
          </Skeleton>
        </Stat>
        <Stat label="Win rate">
          <Skeleton loading={isLoading} className="inline-block">
            {winRate}%
          </Skeleton>
        </Stat>
        <Stat label="Units">
          <Skeleton loading={isLoading} as="span" className="inline-block">
            <span className={clsx(units >= 0 ? 'text-emerald-500' : 'text-rose-500')}>
              {units >= 0 ? '+' : ''}
              {units.toFixed(2)}
            </span>
          </Skeleton>
        </Stat>
      </div>
      {/* No graded plays in range (only pending ones): the chart would be one point
          on an empty grid, which read as a broken grey box. Say so instead (#2142). */}
      {!hasNoPlays &&
        (!isLoading && wins + losses + pushes === 0 ? (
          <p className="mt-4 rounded-lg bg-zinc-50 px-3 py-4 text-center text-sm text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-400">
            No graded plays in this range yet. The chart starts with the first result.
          </p>
        ) : (
          <Suspense fallback={<ResultsChartLoading />}>
            <ResultsChart userId={userId} filters={filters} baseUrl={baseUrl} />
          </Suspense>
        ))}
    </div>
  );
}

/** One header stat tile: an uppercase label over a large tabular value. */
function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="shrink-0">
      <p className="mb-1 font-semibold text-[10px] text-zinc-500 uppercase tracking-wider dark:text-zinc-400">
        {label}
      </p>
      {/* Never wrap: the record ("862-648-18") would otherwise break at its hyphens. The
          stat sizes to its content (shrink-0 in a flex row), so it never clips either. */}
      {/* Fluid so the three stats always fit one line: at the narrowest card width the
          widest real record ("4892-3011-6") still clears, so the row never re-wraps and
          never clips. */}
      <p className="whitespace-nowrap font-bold text-[clamp(1rem,3.4vw,1.5rem)] text-zinc-900 tabular-nums tracking-tight dark:text-white">
        {children}
      </p>
    </div>
  );
}
